/**
 * Pure helper functions for the HeroCMS component.
 * Extracted Feb 17, 2026 to reduce HeroCMS.jsx cyclomatic complexity (was 63).
 * Behavior is byte-for-byte identical to the prior inline implementations.
 */
import { extractYouTubeId } from './extractYouTubeId';

/**
 * Strip HTML tags from a string and clean up CSS artifacts.
 * Falls back to empty string for non-string / falsy input.
 */
export const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';

  // Create a temporary element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  // Get text content (strips all HTML tags)
  let text = tmp.textContent || tmp.innerText || '';

  // Clean up any remaining artifacts (CSS variables, etc.)
  if (text.includes('--tw-') || text.includes('style="') || text.includes('class="')) {
    text = text.replace(/--[\w-]+:\s*[^;]+;?/g, '').trim();
  }

  // Collapse multiple spaces and trim
  return text.replace(/\s+/g, ' ').trim();
};

/**
 * Check if a YouTube ID is valid (11 chars, alphanumeric + - _).
 */
export const isValidYouTubeId = (id) => {
  if (!id || typeof id !== 'string') return false;
  return /^[a-zA-Z0-9_-]{11}$/.test(id.trim());
};

/**
 * Check if a thumbnail URL is a real HTTP(S) URL (not base64 / data: URI).
 */
export const isValidThumbnailUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('data:')) return false;
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Apply CMS block overrides onto the default hero data.
 * Mutates a copy of `defaults` based on visible blocks in cmsContent.
 *
 * NOTE: LATEST_EPISODE block type is deprecated and ignored — episode data
 * comes from `featuredEpisode` or `cmsEpisodes` instead.
 */
export const resolveHeroData = (defaults, cmsContent) => {
  const heroData = { ...defaults };
  if (!cmsContent || !cmsContent.blocksData) return heroData;

  cmsContent.blocksData.forEach((block) => {
    if (!block.isVisible) return;

    if (block.blockType === 'video_background' && block.content.videoUrl) {
      heroData.videoUrl = block.content.videoUrl;
    }

    if (block.blockType === 'hero_text') {
      if (block.content.title) heroData.title = block.content.title;
      if (block.content.titleAccent) heroData.titleAccent = block.content.titleAccent;
      if (block.content.subtitle) heroData.subtitle = block.content.subtitle;
    }

    if (block.blockType === 'cta_buttons' && block.content.buttons) {
      heroData.buttons = block.content.buttons;
      if (block.content.showHeroButtons !== undefined) {
        heroData.showHeroButtons = block.content.showHeroButtons;
      }
    }
  });

  return heroData;
};

/**
 * Build the normalized "latest episode" object from a raw episode record.
 * Returns null if `episode` is falsy.
 */
const buildLatestEpisode = (episode) => {
  if (!episode) return null;
  const extractedYoutubeId = extractYouTubeId(episode.youtubeId || '');
  return {
    title: episode.title || 'Coming Soon',
    series: episode.series || 'Main Show',
    duration: episode.duration || 'TBA',
    publishDate: episode.publishDate || new Date().toISOString(),
    description: episode.description || 'Stay tuned for our latest episode!',
    thumbnailUrl: isValidThumbnailUrl(episode.thumbnail) ? episode.thumbnail : '',
    youtubeId: extractedYoutubeId,
    slug: episode.slug || '',
    // Use CMS showComingSoon field as single source of truth
    showComingSoon: episode.showComingSoon === true,
    hasValidYouTubeId: isValidYouTubeId(extractedYoutubeId),
  };
};

/**
 * Resolve which episode to show in the hero card.
 *
 * Priority:
 *   1. `featuredEpisode` prop (if provided)
 *   2. cmsEpisodes entry with `isFeaturedInHero === true`
 *   3. Most-recent episode from cmsEpisodes (by publishDate)
 *
 * Returns null when no episode data is available (prevents placeholder flash).
 */
export const resolveLatestEpisode = (featuredEpisode, cmsEpisodes) => {
  if (featuredEpisode) {
    return buildLatestEpisode(featuredEpisode);
  }

  if (!cmsEpisodes || cmsEpisodes.length === 0) return null;

  const flagged = cmsEpisodes.find((ep) => ep.isFeaturedInHero === true);
  if (flagged) return buildLatestEpisode(flagged);

  const sorted = [...cmsEpisodes].sort(
    (a, b) => new Date(b.publishDate) - new Date(a.publishDate)
  );
  return buildLatestEpisode(sorted[0]);
};
